/** Calendar day (`YYYY-MM-DD` or a UTC timestamp) without shifting the day across zones. */
export const formatCalendarDate = (value: string, locale = 'en'): string => {
  const ymd = value.length >= 10 ? value.slice(0, 10) : value;
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

/** Instant → `YYYY-MM-DD` in the given IANA zone, for date pickers. */
export const isoToCalendarDate = (value: string, timeZone = 'UTC'): string | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return value.length >= 10 ? value.slice(0, 10) : null;
  }
};

/** UTC ISO timestamps → locale date string for admin tables. */
export const formatDate = (value: string, locale = 'en'): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

export const formatDateTime = (value: string, locale = 'en'): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const EXPONENTS: Record<string, number> = {
  BHD: 3, BIF: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3, JPY: 0,
  KMF: 0, KRW: 0, KWD: 3, LYD: 3, OMR: 3, PYG: 0, RWF: 0, TND: 3, UGX: 0, UYI: 0,
  UYW: 4, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
};

export const decimalPlaces = (currency: string): number =>
  EXPONENTS[currency.toUpperCase()] ?? 2;

export const formatMoney = (
  minorUnits: number,
  currency: string,
  options: { locale?: string; showSymbol?: boolean; parenthesiseNegative?: boolean } = {},
): string => {
  const locale = options.locale ?? 'en';
  const places = decimalPlaces(currency);
  const negative = minorUnits < 0;
  const magnitude = Math.abs(minorUnits) / 10 ** places;

  const formatted = new Intl.NumberFormat(locale, {
    style: options.showSymbol === false ? 'decimal' : 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(magnitude);

  if (!negative) return formatted;
  return options.parenthesiseNegative === false ? `-${formatted}` : `(${formatted})`;
};
